/**
 * The cells that carry the Stock overview's behaviour.
 *
 * Each one is a small interaction with its own rules, which is why they are here rather than
 * inside the table file: click to edit, Enter to commit, Escape to cancel, optimistic write,
 * and a 409 that shows the operator what the other side already had.
 *
 * There is no confirm dialog and no step up on this screen. There is one owner, the audit row
 * carries the diff, and a dialog in front of every price change is how a screen stops getting
 * used. Undo on the toast is the safety net, and it is a real reversible write.
 */
import { useEffect, useRef, useState } from 'react';
import type { StockPageRow } from '@elutide/shared';
import type { AnyStockPageRow } from './types';
import { MoneyText, Pill, StarIcon, WarningIcon } from '@/components';
import { formatCount, formatDaysLeft, formatPercent } from '@/lib/format';
import { STATUS_LABEL } from './constants';

/* ------------------------------------------------------------------ */
/* Shared conflict reading                                             */
/* ------------------------------------------------------------------ */

/**
 * The live quantity a 409 reports.
 *
 * The problem document is RFC 9457, so the number arrives either as an extra top level key
 * or inside `errors[]` against the field that failed the compare. Both are read, and a
 * response carrying neither yields null: the caller then refetches instead of guessing.
 */
export function liveQtyFromProblem(problem: unknown): number | null {
  if (!problem || typeof problem !== 'object') return null;
  const record = problem as Record<string, unknown>;

  for (const key of ['actual_qty', 'qty_after', 'current_qty', 'on_hand']) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) return Math.trunc(value);
  }

  const errors = record.errors;
  if (Array.isArray(errors)) {
    for (const entry of errors) {
      if (!entry || typeof entry !== 'object') continue;
      const field = (entry as { field?: unknown }).field;
      if (field !== 'expected_qty' && field !== 'qty') continue;
      const message = (entry as { message?: unknown }).message;
      const digits = typeof message === 'string' ? /-?\d+/.exec(message)?.[0] : undefined;
      if (digits !== undefined) return Number.parseInt(digits, 10);
    }
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* Input bounds, mirroring the contract                                */
/* ------------------------------------------------------------------ */

/**
 * The same ceilings `StockSetQtyRequest` and `StockSetPriceInlineRequest` enforce.
 *
 * Checked here as well as on the server so a fat fingered 1e10 is refused before it becomes a
 * request, and so an optimistic count never shows a number the server was always going to
 * reject. The server remains the authority; this is not validation, it is not wasting a round
 * trip.
 */
export const MAX_QTY = 1_000_000;
export const MAX_PRICE_CENTS = 100_000_00;

/**
 * A typed integer, or null when the field is empty or not a number.
 *
 * `Number('')` is 0, which is finite and non negative, so a cleared field would otherwise sail
 * past every guard and commit a price of zero to the storefront.
 */
function parseTyped(draft: string, decimals: boolean): number | null {
  const text = draft.trim();
  if (text === '') return null;
  const value = decimals ? Number(text) : Number.parseInt(text, 10);
  if (!Number.isFinite(value)) return null;
  return decimals ? Math.round(value * 100) : value;
}

/* ------------------------------------------------------------------ */
/* Product                                                             */
/* ------------------------------------------------------------------ */

export function ProductCell({ row, band }: { row: AnyStockPageRow; band: string }) {
  return (
    <span className={row.is_visible ? 'stock-product' : 'stock-product stock-row--muted'}>
      <span className="stock-product__band" style={{ background: band }} aria-hidden="true" />
      <span className="stock-product__text">
        <span className="stock-name">
          {row.name}
          {row.size_label ? <span className="o-muted"> {row.size_label}</span> : null}
        </span>
        <span className="o-mono o-muted stock-product__sku">{row.sku ?? 'no SKU'}</span>
      </span>
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* In stock, inline editable                                           */
/* ------------------------------------------------------------------ */

export interface QtyCellProps {
  row: AnyStockPageRow;
  canEdit: boolean;
  /** Commits the typed value. Resolves when the write has settled either way. */
  onCommit: (input: { row: AnyStockPageRow; expectedQty: number; qty: number }) => Promise<void>;
}

/**
 * Click the number, type, Enter commits. Escape cancels. The `expected_qty` sent is the value
 * that was on screen when editing began, not the one that arrived while the operator typed:
 * that is the whole point of compare and set.
 */
export function QtyCell({ row, canEdit, onCommit }: QtyCellProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const expected = useRef(row.on_hand);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const label = `${row.sku ?? row.name}`;

  const start = () => {
    if (!canEdit) return;
    expected.current = row.on_hand;
    setDraft(String(row.on_hand));
    setEditing(true);
  };

  const commit = async () => {
    const typed = parseTyped(draft, false);
    if (typed === null || typed < 0 || typed > MAX_QTY || typed === expected.current) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      await onCommit({ row, expectedQty: expected.current, qty: typed });
    } finally {
      setBusy(false);
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        className="o-input stock-qty__input"
        inputMode="numeric"
        value={draft}
        disabled={busy}
        aria-label={`In stock for ${label}`}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            void commit();
          }
          if (event.key === 'Escape') {
            event.preventDefault();
            setEditing(false);
          }
        }}
        onBlur={() => {
          if (!busy) setEditing(false);
        }}
      />
    );
  }

  return (
    <span className="stock-qty">
      <button
        type="button"
        className={canEdit ? 'stock-inline' : 'stock-inline stock-inline--locked'}
        disabled={!canEdit}
        onClick={start}
        aria-label={canEdit ? `Edit in stock for ${label}` : undefined}
      >
        <span className={row.on_hand <= 0 ? 'o-num stock-out' : 'o-num'}>
          {formatCount(row.on_hand)}
        </span>
      </button>
      {row.inbound_qty > 0 ? (
        <span
          className="stock-inbound"
          title={row.inbound_eta ? `ETA ${row.inbound_eta}` : undefined}
        >
          {`(+${row.inbound_qty} inbound)`}
        </span>
      ) : null}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* 30 day sales, bar and days left pill                                */
/* ------------------------------------------------------------------ */

export interface SalesCellProps {
  row: AnyStockPageRow;
  /** The largest 30 day unit count on the page, so the bars are comparable. */
  peakUnits: number;
  criticalDays: number;
  lowDays: number;
}

export function daysLeftTone(
  days: number | null,
  criticalDays: number,
  lowDays: number,
): 'ok' | 'warn' | 'crit' | 'neutral' {
  if (days === null) return 'neutral';
  if (days < criticalDays) return 'crit';
  if (days <= lowDays) return 'warn';
  return 'ok';
}

export function SalesCell({ row, peakUnits, criticalDays, lowDays }: SalesCellProps) {
  const tone = daysLeftTone(row.days_left, criticalDays, lowDays);
  const fraction = peakUnits > 0 ? Math.max(0, Math.min(1, row.units_30d / peakUnits)) : 0;

  return (
    <span className="stock-sales">
      <span className="o-num stock-sales__units">{formatCount(row.units_30d)}</span>
      <span className="stock-bar" aria-hidden="true">
        <span
          className="stock-bar__fill stock-bar__fill--units"
          style={{ width: `${(fraction * 100).toFixed(0)}%` }}
        />
      </span>
      {/* Colour is never the only signal: the pill carries the status word as well. */}
      <Pill tone={tone === 'neutral' ? 'neutral' : tone}>
        <span className="stock-sales__status">{STATUS_LABEL[row.status]}</span>{' '}
        <span>{formatDaysLeft(row.days_left)}</span>
      </Pill>
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Sale price, inline editable                                         */
/* ------------------------------------------------------------------ */

export interface PriceCellProps {
  row: StockPageRow;
  canEdit: boolean;
  onCommit: (input: { row: StockPageRow; priceCents: number }) => Promise<void>;
}

export function PriceCell({ row, canEdit, onCommit }: PriceCellProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const label = `${row.sku ?? row.name}`;

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const start = () => {
    if (!canEdit) return;
    setDraft((row.price_cents / 100).toFixed(2));
    setEditing(true);
  };

  const commit = async () => {
    const typed = parseTyped(draft, true);
    if (typed === null || typed < 0 || typed > MAX_PRICE_CENTS || typed === row.price_cents) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      await onCommit({ row, priceCents: typed });
    } finally {
      setBusy(false);
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        className="o-input stock-price__input"
        inputMode="decimal"
        value={draft}
        disabled={busy}
        aria-label={`Sale price for ${label}`}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            void commit();
          }
          if (event.key === 'Escape') {
            event.preventDefault();
            setEditing(false);
          }
        }}
        onBlur={() => {
          if (!busy) setEditing(false);
        }}
      />
    );
  }

  return (
    <button
      type="button"
      className={canEdit ? 'stock-inline' : 'stock-inline stock-inline--locked'}
      disabled={!canEdit}
      onClick={start}
      aria-label={canEdit ? `Edit sale price for ${label}` : undefined}
    >
      <MoneyText cents={row.price_cents} />
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Cost and margin                                                     */
/* ------------------------------------------------------------------ */

const SOFT_COST_SOURCE = new Set(['manual', 'estimate']);

export interface CostCellProps {
  row: StockPageRow;
  /** Opens the breakdown behind this number. */
  onOpenBreakdown: (row: StockPageRow) => void;
}

/**
 * Cost is the one number on this row nobody types.
 *
 * It is derived from received batches, so an inline editor here would be a lie: the way to
 * change it is to fix a batch cost or enter an override, both of which live elsewhere. The
 * click therefore opens the arithmetic instead of a field, and the affordance is deliberately
 * a different one: the value underlines on hover rather than growing the box-and-border the
 * editable cells use, so a hand that knows the qty and price cells does not expect a caret
 * here.
 *
 * The margin and the source marker sit outside the button. They are context for the cost,
 * not part of the thing being opened, and a button wrapping the whole cell would read the
 * whole lot out as one control name.
 */
export function CostCell({ row, onOpenBreakdown }: CostCellProps) {
  if (row.wac_cents === null) {
    return <span className="o-muted">no cost</span>;
  }
  const soft = SOFT_COST_SOURCE.has(row.wac_source);
  const label = `${row.sku ?? row.name}`;
  return (
    <span className="stock-cost">
      <button
        type="button"
        className="stock-costlink"
        onClick={() => onOpenBreakdown(row)}
        aria-label={`Cost breakdown for ${label}`}
      >
        <MoneyText cents={row.wac_cents} showCents />
      </button>
      {soft ? (
        <span className="stock-cost__mark" title={`Cost source: ${row.wac_source}`}>
          <WarningIcon />
          <span className="o-muted">{row.wac_source}</span>
        </span>
      ) : null}
      <span className="o-muted stock-cost__margin">
        {row.margin_pct === null ? 'no margin' : formatPercent(row.margin_pct)}
      </span>
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Best supplier price                                                 */
/* ------------------------------------------------------------------ */

export interface BestPriceCellProps {
  row: StockPageRow;
  onOpenQuotes: (row: StockPageRow) => void;
}

/** A row with no quote renders a dash. Zero would be a lie and a crash would be worse. */
export function BestPriceCell({ row, onOpenQuotes }: BestPriceCellProps) {
  if (row.best_price_cents === null) {
    return (
      <span className="o-muted" title="No supplier quote on file for this SKU">
        -
      </span>
    );
  }

  return (
    <button
      type="button"
      className="stock-inline stock-best"
      onClick={() => onOpenQuotes(row)}
      aria-label={`Supplier quotes for ${row.sku ?? row.name}, ${row.quote_count} on file`}
    >
      <span className="stock-best__price">
        <MoneyText cents={row.best_price_cents} showCents />
        {row.best_price_negotiated ? (
          <>
            <StarIcon />
            <span className="o-visually-hidden">negotiated</span>
          </>
        ) : null}
      </span>
      <span className="o-muted stock-best__supplier">
        {row.best_price_supplier ?? 'supplier unknown'}
      </span>
    </button>
  );
}
